// The TASK-THREAD controller — orchestration for the one enduring shared Session owned by a Task. Unlike
// `useChat`, it NEVER mints/selects a Session, opens private-chat history, or writes a resume pointer: every
// send is path-scoped by Task id and the server resolves the canonical Session. The Task-detail snapshot is
// authoritative; streamed output/activity is ephemeral preview state and every end/error re-reads the Task.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import type { ChatStreamHandlers } from './api-client.ts';
import {
  appendStreamText,
  emptyChatStream,
  reconcileStreamActivity,
  replaceStreamText,
  replaceStreamPlan,
  updateStreamReasoning,
} from './chat-turns.ts';
import type { ChatStreamState } from './chat-turns.ts';
import type { TaskDetailProjection } from './know-projections.ts';
import type { ChatSendResult } from './projections.ts';
import { useClosePreview, useTaskContinue, useTaskInterrupt } from './task-interrupt.ts';
import { useQueuedRunObserver } from './task-thread-queue.ts';
import { notifyTaskProjectionsChanged } from './task-projection-events.ts';
import {
  useRetainedTaskComposerState,
  type ComposerStash,
  type RetainedComposerState,
} from './task-composer-retention.ts';
import {
  mintTaskRequest,
  reusablePendingTaskRequest,
  type PendingTaskRequest,
  type TaskComposerRetention,
  type TaskComposerRetentionEvent,
} from './task-request-identity.ts';

const QUEUED_NOTE =
  'Ritona is finishing the current update — yours is queued on this task and will follow.';
// A server REFUSAL is durable on the append-only thread: the message + a failed-run marker are recorded
// even though the agent could not act on it (audit F4). So the copy must NOT claim "nothing was actioned"
// — it tells the human their update IS on the thread and could not be actioned. The human-readable note
// is preferred over this fallback; the raw module reason code is never surfaced (GI-07).
const REFUSED_NOTE = 'Your update is on the task thread, but Ritona could not act on it.';
// A transport CRASH is different: the update may never have reached the server, so nothing is durably
// recorded and "could not reach the task" stays honest.
const TRANSPORT_NOTE = 'Your update could not reach the task — nothing was actioned.';
const RETENTION_CAPACITY_NOTE =
  'Too many Task updates are awaiting delivery confirmation. Check those Tasks before sending another update.';

export type TaskThreadPreview = ChatStreamState;
export type { ComposerStash } from './task-composer-retention.ts';
const EMPTY_TASK_PREVIEW: TaskThreadPreview = emptyChatStream();

interface MutableValue<T> {
  current: T;
}

export interface TaskThreadController {
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  readonly sending: boolean;
  readonly note: string | null;
  readonly canMessage: boolean;
  readonly preview: TaskThreadPreview;
  readonly send: () => Promise<void>;
  /** The run a Stop would target — null when nothing of this task's is in flight yet. */
  readonly activeRunId: string | null;
  /** Stop the task's live run: the same human-only interrupt the chat dock's Stop uses. */
  readonly interrupt: () => Promise<void>;
  readonly continuingRunId: string | null;
  readonly continueRun: (runId: string) => Promise<void>;
}

export interface UseTaskThreadInput {
  readonly taskId: string;
  readonly detail: TaskDetailProjection;
  readonly reload: () => void;
  /** Survives the Task body's loading-state remount so one failed stream cannot reopen in a loop. */
  readonly streamFailureFence: MutableValue<string | null>;
  /** Survives the same remount so a refusal note + the preserved draft render after the reload (N5). */
  readonly composerStash: MutableValue<ComposerStash>;
  /** Reactive page-memory for bounded draft + exact uncertain-request correlation only. */
  readonly retention: TaskComposerRetention | null;
  readonly onRetentionEvent: (event: TaskComposerRetentionEvent) => boolean;
}

/** The newest running run in an oldest-first snapshot (serialisation means there is at most one in practice). */
function latestRunningRunId(detail: TaskDetailProjection): string | null {
  for (let index = detail.runs.length - 1; index >= 0; index -= 1) {
    const run = detail.runs[index];
    if (run?.state === 'running') return run.id;
  }
  return null;
}

/** Stream callbacks only mutate ephemeral preview state; end/error always reconcile to the Task snapshot. */
function previewHandlers(
  runId: string,
  setPreview: Dispatch<SetStateAction<TaskThreadPreview>>,
  reconcile: () => void,
): ChatStreamHandlers {
  return {
    onDelta: (_sequence, text) =>
      setPreview((current) =>
        current.runId === runId ? appendStreamText(current, text) : current,
      ),
    onSnapshot: (text) =>
      setPreview((current) =>
        current.runId === runId ? replaceStreamText(current, text) : current,
      ),
    onReasoning: (text) =>
      setPreview((current) =>
        current.runId === runId ? updateStreamReasoning(current, text) : current,
      ),
    onReasoningSummary: (text) =>
      setPreview((current) =>
        current.runId === runId
          ? updateStreamReasoning(current, text, 'provider_summary')
          : current,
      ),
    onPlan: (plan) =>
      setPreview((current) =>
        current.runId === runId ? replaceStreamPlan(current, plan) : current,
      ),
    onActivity: (row) =>
      setPreview((current) =>
        current.runId === runId ? reconcileStreamActivity(current, row) : current,
      ),
    onEnd: reconcile,
    onError: reconcile,
    onRefused: () =>
      setPreview((current) => (current.runId === runId ? replaceStreamText(current, '') : current)),
  };
}

interface OpenPreviewInput {
  readonly taskId: string;
  readonly reload: () => void;
  readonly mounted: MutableValue<boolean>;
  readonly setPreview: Dispatch<SetStateAction<TaskThreadPreview>>;
  readonly stop: MutableValue<(() => void) | null>;
  readonly generation: MutableValue<number>;
  readonly activeRun: MutableValue<string | null>;
  readonly awaitingSnapshotRun: MutableValue<string | null>;
}

/** Stable stream opener. Generation checks make every callback after switch/unmount inert. */
function useOpenTaskPreview({
  taskId,
  reload,
  mounted,
  setPreview,
  stop,
  generation,
  activeRun,
  awaitingSnapshotRun,
}: OpenPreviewInput): (runId: string) => void {
  const api = useSurfaceApi();
  return useCallback(
    (runId: string): void => {
      if (activeRun.current === runId && stop.current !== null) return;
      generation.current += 1;
      const openingGeneration = generation.current;
      stop.current?.();
      stop.current = null;
      activeRun.current = runId;
      setPreview(emptyChatStream(runId));
      const reconcile = (): void => {
        if (!mounted.current || generation.current !== openingGeneration) return;
        awaitingSnapshotRun.current = runId;
        stop.current?.();
        stop.current = null;
        activeRun.current = null;
        // Keep the terminal live sentence mounted until the refreshed Task snapshot replaces it. Summary,
        // finishing, and end can arrive in one chunk; clearing synchronously erased them before React painted.
        reload();
        notifyTaskProjectionsChanged();
      };
      const unsubscribe = api.streamTaskRun(
        taskId,
        runId,
        previewHandlers(runId, setPreview, reconcile),
      );
      if (generation.current === openingGeneration) stop.current = unsubscribe;
      else unsubscribe();
    },
    [activeRun, api, awaitingSnapshotRun, generation, mounted, reload, setPreview, stop, taskId],
  );
}

interface PreviewController {
  readonly preview: TaskThreadPreview;
  readonly openPreview: (runId: string) => void;
  readonly closePreview: (runId: string) => void;
}

/** Own one active Task stream and auto-attach when a snapshot already contains a running run. */
function useTaskPreview(
  { taskId, detail, reload, streamFailureFence }: UseTaskThreadInput,
  mounted: MutableValue<boolean>,
): PreviewController {
  const [preview, setPreview] = useState<TaskThreadPreview>(EMPTY_TASK_PREVIEW);
  const stop = useRef<(() => void) | null>(null);
  const generation = useRef(0);
  const activeRun = useRef<string | null>(null);
  const awaitingSnapshotRun = streamFailureFence;
  const openPreview = useOpenTaskPreview({
    taskId,
    reload,
    mounted,
    setPreview,
    stop,
    generation,
    activeRun,
    awaitingSnapshotRun,
  });
  const closePreview = useClosePreview({
    fence: awaitingSnapshotRun,
    generation,
    stop,
    activeRun,
    setPreview,
  });
  const runningRunId = useMemo(() => latestRunningRunId(detail), [detail]);
  useEffect(() => {
    // Keep the fence when a reconcile read still reports the SAME run as running: a persistent transport
    // failure must not create a subscribe → error → reload loop. Completion or a newer run clears it.
    if (awaitingSnapshotRun.current !== null && awaitingSnapshotRun.current !== runningRunId) {
      const reconciledRunId = awaitingSnapshotRun.current;
      awaitingSnapshotRun.current = null;
      setPreview((current) => (current.runId === reconciledRunId ? EMPTY_TASK_PREVIEW : current));
    }
  }, [awaitingSnapshotRun, runningRunId, setPreview]);
  useEffect(() => {
    if (runningRunId !== null && awaitingSnapshotRun.current !== runningRunId) {
      openPreview(runningRunId);
    }
  }, [awaitingSnapshotRun, openPreview, runningRunId]);
  useEffect(
    () => () => {
      generation.current += 1;
      stop.current?.();
      stop.current = null;
      activeRun.current = null;
    },
    [activeRun, generation, stop, taskId],
  );
  return { preview, openPreview, closePreview };
}

interface ComposerController {
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  readonly sending: boolean;
  readonly note: string | null;
  readonly send: () => Promise<void>;
}

interface TaskSendResultContext {
  readonly input: UseTaskThreadInput;
  readonly message: string;
  readonly openPreview: (runId: string) => void;
  readonly setDraft: (value: string) => void;
  readonly setNote: (value: string | null) => void;
}

function preparePendingTaskSend(
  input: UseTaskThreadInput,
  message: string,
  setNote: (value: string | null) => void,
): PendingTaskRequest | undefined {
  try {
    const pending =
      reusablePendingTaskRequest(
        input.composerStash.current.pendingRequest,
        input.taskId,
        message,
      ) ?? mintTaskRequest(input.taskId, message);
    input.composerStash.current.pendingRequest = pending;
    const retained = input.onRetentionEvent({
      kind: 'request_started',
      draft: input.composerStash.current.draft,
      pendingRequest: pending,
    });
    if (!retained) {
      input.composerStash.current.pendingRequest = null;
      setNote(RETENTION_CAPACITY_NOTE);
      return undefined;
    }
    return pending;
  } catch {
    setNote('A secure Task request could not be prepared.');
    return undefined;
  }
}

function applyTaskSendResult(result: ChatSendResult, context: TaskSendResultContext): void {
  const { input, message, openPreview, setDraft, setNote } = context;
  if (result.kind === 'started') {
    input.composerStash.current.queuedRun = null;
    setDraft('');
    input.reload();
    openPreview(result.runId);
    return;
  }
  if (result.kind === 'queued') {
    input.composerStash.current.queuedRun = {
      baselineRunIds: input.detail.runs.map((run) => run.id),
      message,
      attempts: 0,
    };
    setDraft('');
    setNote(QUEUED_NOTE);
    return;
  }
  input.composerStash.current.queuedRun = null;
  // Prefer the transport's sanitised note; never surface the raw owning-module reason code (GI-07).
  setNote(result.note ?? REFUSED_NOTE);
  input.reload();
}

function reconcileUnknownTaskSend(
  input: UseTaskThreadInput,
  mounted: MutableValue<boolean>,
  setNote: (value: string | null) => void,
): void {
  if (!mounted.current) return;
  // Retain the pending id: the operation may already have reached the server.
  input.composerStash.current.queuedRun = null;
  setNote(TRANSPORT_NOTE);
  input.reload();
}

/** Own Task-composer state and route exactly one taskId/message/requestId tuple per accepted submit (N5: the
 *  draft/note survive the reload remount via the write-through stash). */
function useTaskComposer(
  input: UseTaskThreadInput,
  mounted: MutableValue<boolean>,
  openPreview: (runId: string) => void,
  blocked: boolean,
  composer: RetainedComposerState,
): ComposerController {
  const api = useSurfaceApi();
  const { draft, setDraft, note, setNote } = composer;
  const [sending, setSending] = useState(false);
  const send = useCallback(async (): Promise<void> => {
    const message = draft.trim();
    if (message.length === 0 || sending || blocked || !input.detail.canMessage) return;
    const pending = preparePendingTaskSend(input, message, setNote);
    if (pending === undefined) return;
    setSending(true);
    setNote(null);
    try {
      const result = await api.sendTaskMessage(input.taskId, message, pending.requestId);
      input.onRetentionEvent({
        kind: result.kind === 'refused' ? 'request_refused' : 'request_accepted',
        pendingRequest: pending,
      });
      if (input.composerStash.current.pendingRequest === pending) {
        input.composerStash.current.pendingRequest = null;
      }
      if (!mounted.current) return;
      applyTaskSendResult(result, { input, message, openPreview, setDraft, setNote });
    } catch {
      input.onRetentionEvent({ kind: 'request_unknown', pendingRequest: pending });
      reconcileUnknownTaskSend(input, mounted, setNote);
    } finally {
      if (mounted.current) setSending(false);
    }
  }, [api, blocked, draft, input, mounted, openPreview, sending, setDraft, setNote]);
  return { draft, setDraft, sending, note, send };
}

/** Keep async callbacks honest across task switches and modal unmounts. */
function useMountedBoundary(taskId: string): MutableValue<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, [taskId]);
  return mounted;
}

function useStableTaskThreadInput(source: UseTaskThreadInput): UseTaskThreadInput {
  const { taskId, detail, reload, streamFailureFence, composerStash, retention, onRetentionEvent } =
    source;
  return useMemo(
    () => ({
      taskId,
      detail,
      reload,
      streamFailureFence,
      composerStash,
      retention,
      onRetentionEvent,
    }),
    [composerStash, detail, onRetentionEvent, reload, retention, streamFailureFence, taskId],
  );
}

/** The single public owner of task-thread send/stream/reconcile behavior. */
export function useTaskThread(source: UseTaskThreadInput): TaskThreadController {
  const input = useStableTaskThreadInput(source);
  const { taskId, detail, reload, composerStash, retention, onRetentionEvent } = input;
  const mounted = useMountedBoundary(taskId);
  const { preview, openPreview, closePreview } = useTaskPreview(input, mounted);
  const composerState = useRetainedTaskComposerState(composerStash, retention, onRetentionEvent);
  useQueuedRunObserver(input, mounted, composerState);
  // The current stream/snapshot and a queued admission each remain visible owners of this composer until
  // terminal reconciliation or the bounded queued observer settles them.
  const activeRunId = preview.runId ?? latestRunningRunId(detail);
  const queued = composerStash.current.queuedRun !== null;
  const requestInFlight = retention?.status === 'in_flight';
  const composer = useTaskComposer(
    input,
    mounted,
    openPreview,
    activeRunId !== null || queued || requestInFlight,
    composerState,
  );
  const interrupt = useTaskInterrupt({
    activeRunId,
    closePreview,
    reload,
    setNote: composerState.setNote,
    mounted,
  });
  const continuation = useTaskContinue({
    openPreview,
    setNote: composerState.setNote,
    mounted,
  });
  return {
    ...composer,
    sending: composer.sending || activeRunId !== null || queued || requestInFlight,
    canMessage: detail.canMessage,
    preview,
    activeRunId,
    interrupt,
    ...continuation,
  };
}
